import { ApiStreamEventType } from "./streamProtocol";
import {
    HEALTHY_STREAM_MS,
    isConnectionLostEvent,
    isStreamRefused,
    MAX_QUICK_REOPENS,
    reopenDelayMs,
} from "./streamReopen";
import type {
    ApiAnsweredQuestion,
    ApiCreateRunRequest,
    ApiCreateRunResponse,
    ApiStreamEvent,
    DecideRunActionResult,
} from "../api/run";
import type {
    ActivityPartPayload,
    ApiMessagePart,
    MarvinAction,
    MarvinQuestion,
} from "./messageParts";
import { partEventApplies, readMessageParts } from "./partReader";
import { ApiMessageTypes } from "../shared/interfaces";
import type { ApiConversation, ApiFile, ApiMessage, ApiReference } from "../shared/interfaces";
import { MARVIN_USER_ID } from "../shared/constants";
import { preprocessMessageMarkdown } from "../util/markdownStreamUtils";
import {
    actionMarkdown,
    normalizedSegments,
    questionMarkdown,
    segmentsFromProcessedMarkdown,
} from "./engineMarkdown";
import {
    buildEngineTurn,
    createTurnClock,
    savedMessageTurn,
    turnPhaseOf,
    type EngineTurn,
    type TurnClock,
} from "./turn/engineTurn";
import {
    activityPartsToTrace,
    handleStreamEvent,
    initialStreamState,
    partsMarkdown,
    partsReferenceMap,
    partsFiles,
    streamProtocolForEventType,
} from "../hooks/streamReducer";
import type { RunStreamState, StreamProtocol } from "../hooks/streamReducer";
import {
    ConversationReferenceProcessor as ReferenceProcessor,
    resolveThinkingReferences,
    stripBold,
} from "./referenceProjection";
import {
    initialStreamState as initialLegacyStreamState,
    getMarkdownContent as legacyStreamMarkdown,
    handleStreamEvent as reduceLegacyStreamEvent,
} from "./legacyStreamReducer";
import type { RunStreamState as LegacyRunStreamState } from "./legacyStreamReducer";

export interface ConversationRuntimePorts {
    listConversations(): Promise<ApiConversation[]>;
    getConversation(id: string): Promise<ApiConversation>;
    createConversation(
        message: string,
        messageType: string,
        documentFilters?: string[],
    ): Promise<ApiConversation>;
    createRun(request: ApiCreateRunRequest): Promise<ApiCreateRunResponse>;
    streamRun(
        runId: string,
        onEvent: (event: ApiStreamEvent) => void,
        options?: { afterSeq?: number },
    ): () => void;
    cancelRun(runId: string): Promise<void>;
    decideAction(runId: string, actionId: string, approve: boolean): Promise<DecideRunActionResult>;
}

export interface EngineConversationSummary {
    id: string;
    title: string;
    lastActivityAt: string;
    /** `lastActivityAt` in ms since 1970, so native sorts without parsing dates (`withActivityTime`). */
    lastActivityAtMs?: number;
}

/** Native renders markdown only: the engine never sends a widget (segmentsFromProcessedMarkdown). */
export interface EngineSegment {
    kind: "markdown";
    content: string;
}

export interface EngineMessage {
    id: string;
    role: "user" | "marvin";
    type: "markdown" | "table" | "error";
    /**
     * Deprecated: the answer as markdown only. Kept for one release beside `turn`, so a native
     * build that predates `turn` still draws the answer; remove once no admitted build reads it.
     */
    segments: EngineSegment[];
    createdAt: string;
    /** `createdAt` in ms since 1970, so native orders messages without parsing dates (`toEngineConversation`). */
    createdAtMs?: number;
    /** The run that wrote this Marvin message, when the server recorded one. */
    runId?: string;
    /** The persisted run status; "streaming" while that run is still answering. */
    status?: "ok" | "cancelled" | "error" | "streaming";
    /** A Marvin message's turn: its steps, answer and cards (`turn/engineTurn.ts`). Absent on a message without parts. */
    turn?: EngineTurn;
}

export interface EngineConversation {
    id: string;
    title: string;
    messages: EngineMessage[];
}

export interface EngineThinking {
    isThinking: boolean;
    durationMs: number | null;
}

export interface EngineStreamSnapshot {
    commandId: string;
    conversationId: string;
    userQuery: string;
    /** Deprecated with `segments` and `statusText`: kept for one release beside `turn`. */
    thinking: EngineThinking;
    /** Deprecated: the answer as markdown only, kept for one release beside `turn`. */
    segments: EngineSegment[];
    status: "creating" | "streaming" | "ok" | "cancelled" | "error";
    errorMessage?: string;
    /** Deprecated: one progress line, kept for one release beside `turn`'s header and steps. */
    statusText: string;
    runId?: string;
    /** The last run event applied, with `runId`: where a reopened stream resumes. */
    lastSeq?: number;
    /** The run's turn so far: header, steps, answer and cards (`turn/engineTurn.ts`). Absent on a legacy stream. */
    turn?: EngineTurn;
}

export interface RuntimeMessage extends EngineMessage {
    parts: ApiMessagePart[];
    errorMessage?: string;
}

export interface RuntimeConversation {
    id: string;
    title: string;
    messages: RuntimeMessage[];
}

export interface ConversationRunDraft {
    commandId: string;
    conversationId: string;
    text: string;
    files?: string[];
    /** "deep": a Deep research turn (`ApiCreateRunRequest.depth`). */
    depth?: "deep";
    /** The question card this message answers (`ApiCreateRunRequest.answersQuestion`): the
     *  server records the pick on the card and applies what the option does (a source it adds
     *  to the conversation). Dropped once a run is created, since that claimed the card. */
    answersQuestion?: ApiAnsweredQuestion;
    /** Resolved server-side document filter tokens carried from the original
     *  NewConversationRunDraft so a retry re-sends the same scope. Ignored
     *  when `prepareDocumentFilters` is also set. */
    documentFilters?: string[];
    /** Lazily resolves `documentFilters` -- see NewConversationRunDraft. Cleared
     *  (replaced by the resolved `documentFilters`) once it has run once. */
    prepareDocumentFilters?: () => Promise<string[] | undefined>;
    createConversation?: boolean;
    prepareFiles?: () => Promise<string[]>;
}

export interface NewConversationRunDraft {
    commandId: string;
    text: string;
    files?: string[];
    depth?: "deep";
    /** Resolved server-side document filter tokens for the new conversation
     *  (already-resolved: explicit source selection or the tenant default,
     *  expanded by the caller). Omitted/undefined means unscoped. Ignored
     *  when `prepareDocumentFilters` is also set. */
    documentFilters?: string[];
    /** Lazily resolves `documentFilters`, mirroring `prepareFiles`: invoked
     *  AFTER the "creating" snapshot publishes (so status feedback is never
     *  delayed by e.g. a settings round-trip) and awaited before the
     *  conversation is created. */
    prepareDocumentFilters?: () => Promise<string[] | undefined>;
    prepareFiles?: () => Promise<string[]>;
}

export interface AttachConversationRun {
    commandId: string;
    conversationId: string;
    runId: string;
    /** Unknown when native re-attaches a run after the engine page reloaded. */
    marvinMessageId?: string;
    userQuery: string;
}
export interface ConversationRuntimeActiveRun {
    commandId: string;
    conversationId: string;
    runId: string | null;
    marvinMessageId: string | null;
    userQuery: string;
    parts: ApiMessagePart[];
    lastSeq: number;
    status: EngineStreamSnapshot["status"];
    snapshot: EngineStreamSnapshot;
}

export interface ConversationRuntimeState {
    conversations: EngineConversationSummary[];
    selectedConversationId: string | null;
    selectedConversation: RuntimeConversation | null;
    activeRun: ConversationRuntimeActiveRun | null;
    error: string | null;
    retryDraft: ConversationRunDraft | null;
}

export interface ConversationReferenceTarget {
    documentId?: string;
    sectionGroupId?: string;
}

export interface ConversationReferenceResolver {
    resolve(references: ApiReference[]): Promise<void>;
    getSectionTarget(sectionId: string): ConversationReferenceTarget | null | undefined;
    getFileTarget(fileId: string): ConversationReferenceTarget | null | undefined;
}

export type ConversationRuntimeEvent =
    | {
          type: "conversations";
          commandId: string;
          conversations: EngineConversationSummary[];
      }
    | {
          type: "conversation";
          commandId: string;
          conversation: RuntimeConversation;
          /** Its citations are still resolving: the same conversation follows with their targets. */
          provisional?: boolean;
      }
    | { type: "snapshot"; snapshot: EngineStreamSnapshot }
    | { type: "failure"; commandId: string; message: string };

export interface ConversationRuntimeOptions {
    now?: () => number;
    fallbackStatusText?: () => string;
    referenceResolver?: ConversationReferenceResolver;
    /**
     * Leaves the deprecated `segments` and `statusText` empty wherever a `turn` says the same: on a
     * snapshot with a turn, and on a Marvin message with a turn that is not an error (an error
     * message's words stay in its segments). For the iOS engine, which ships inside the app with
     * the native build that reads it, so every reader has a turn; it halves what crosses the bridge.
     */
    omitLegacyBesideTurn?: boolean;
}

export interface ConversationRuntime {
    getState(): ConversationRuntimeState;
    subscribe(listener: () => void): () => void;
    subscribeEvents(listener: (event: ConversationRuntimeEvent) => void): () => void;
    list(commandId: string): Promise<boolean>;
    load(commandId: string, conversationId: string): Promise<boolean>;
    send(draft: ConversationRunDraft): Promise<boolean>;
    createAndRun(draft: NewConversationRunDraft): Promise<boolean>;
    attach(run: AttachConversationRun): void;
    /** Reopens the active run's stream after the last event it applied. */
    resume(): void;
    cancel(): Promise<void>;
    retry(): Promise<boolean>;
    decideAction(runId: string, actionId: string, approve: boolean): Promise<DecideRunActionResult>;
    reset(): void;
    dispose(): void;
}

function isValidMessage(value: unknown): value is ApiMessage {
    if (
        typeof value !== "object" ||
        value === null ||
        Array.isArray(value) ||
        !("id" in value) ||
        !("message" in value) ||
        !("messageType" in value) ||
        !("user" in value) ||
        !("files" in value) ||
        !("createdAt" in value)
    ) {
        return false;
    }
    const user = value.user;
    if (
        typeof user !== "object" ||
        user === null ||
        Array.isArray(user) ||
        !("id" in user) ||
        typeof user.id !== "string"
    ) {
        return false;
    }
    if (
        typeof value.id !== "string" ||
        typeof value.message !== "string" ||
        typeof value.messageType !== "string" ||
        !Array.isArray(value.files)
    ) {
        return false;
    }
    if (
        "requestId" in value &&
        value.requestId !== undefined &&
        value.requestId !== null &&
        typeof value.requestId !== "string"
    ) {
        return false;
    }
    const createdAt = value.createdAt;
    if (
        !(createdAt instanceof Date) &&
        (typeof createdAt !== "string" || Number.isNaN(new Date(createdAt).getTime()))
    ) {
        return false;
    }
    // The parts are read tolerantly (partReader.ts): an unreadable part never fails the message.
    return true;
}

function assertConversation(value: unknown): asserts value is ApiConversation {
    if (
        typeof value !== "object" ||
        value === null ||
        Array.isArray(value) ||
        !("id" in value) ||
        typeof value.id !== "string" ||
        !("title" in value) ||
        typeof value.title !== "string" ||
        !("messages" in value) ||
        !Array.isArray(value.messages) ||
        !value.messages.every(isValidMessage)
    ) {
        throw new Error("Invalid conversation payload");
    }
}

function isValidStreamEvent(value: unknown): value is ApiStreamEvent {
    if (
        typeof value !== "object" ||
        value === null ||
        Array.isArray(value) ||
        !("type" in value) ||
        typeof value.type !== "string" ||
        !("seq" in value) ||
        !Number.isSafeInteger(value.seq) ||
        Number(value.seq) < 0 ||
        !("data" in value) ||
        typeof value.data !== "object" ||
        value.data === null ||
        Array.isArray(value.data)
    ) {
        return false;
    }
    const data = value.data;
    switch (value.type) {
        // A part event's content is read tolerantly: see partEventApplies (partReader.ts).
        case ApiStreamEventType.PartCreated:
        case ApiStreamEventType.PartUpdated:
        case ApiStreamEventType.PartCompleted:
            return true;
        case ApiStreamEventType.Done:
            return (
                "status" in data &&
                (data.status === "ok" || data.status === "cancelled" || data.status === "error")
            );
        case ApiStreamEventType.Error:
            return "error" in data && typeof data.error === "string";
        case ApiStreamEventType.Step:
            return (
                "code" in data &&
                typeof data.code === "string" &&
                "message" in data &&
                typeof data.message === "string"
            );
        case ApiStreamEventType.ContentBlockStart:
        case ApiStreamEventType.ContentBlockStop:
            return "blockId" in data && typeof data.blockId === "string";
        case ApiStreamEventType.ContentBlockDelta:
            return (
                "blockId" in data &&
                typeof data.blockId === "string" &&
                "delta" in data &&
                typeof data.delta === "string"
            );
        case ApiStreamEventType.ThinkingDelta:
            return "delta" in data && typeof data.delta === "string";
        case ApiStreamEventType.ThinkingStop:
            return "durationMs" in data && typeof data.durationMs === "number";
        case ApiStreamEventType.ReferencesStart:
        case ApiStreamEventType.ReferencesStop:
            return (
                "references" in data &&
                typeof data.references === "object" &&
                data.references !== null &&
                "files" in data &&
                Array.isArray(data.files)
            );
        case ApiStreamEventType.ReferencesDelta:
            return (
                "references" in data &&
                typeof data.references === "object" &&
                data.references !== null &&
                (!("files" in data) || data.files === undefined || Array.isArray(data.files))
            );
        case ApiStreamEventType.ToolCallStart:
            return (
                "name" in data &&
                typeof data.name === "string" &&
                "call_id" in data &&
                typeof data.call_id === "string"
            );
        case ApiStreamEventType.ToolCallDelta:
            return (
                "call_id" in data &&
                typeof data.call_id === "string" &&
                "delta" in data &&
                typeof data.delta === "string"
            );
        case ApiStreamEventType.ToolCallStop:
            return "call_id" in data && typeof data.call_id === "string";
        case ApiStreamEventType.ToolResult:
            return (
                "name" in data &&
                typeof data.name === "string" &&
                "call_id" in data &&
                typeof data.call_id === "string" &&
                "result" in data &&
                typeof data.result === "string"
            );
        case ApiStreamEventType.ThinkingStart:
        case ApiStreamEventType.LlmStart:
        case ApiStreamEventType.LlmStop:
            return true;
        case ApiStreamEventType.Retrieval:
            return (
                "topDown" in data &&
                typeof data.topDown === "number" &&
                "bottomUp" in data &&
                typeof data.bottomUp === "number" &&
                "flatToc" in data &&
                typeof data.flatToc === "number"
            );
        case ApiStreamEventType.ToolSuggestion:
        case ApiStreamEventType.ArtifactReady:
        case ApiStreamEventType.AskUser:
        case ApiStreamEventType.AskUserAnswered:
        case ApiStreamEventType.ActionProposed:
        case ApiStreamEventType.ActionResolved:
        case ApiStreamEventType.MemoryUpdated:
        case ApiStreamEventType.NavigationSuggested:
        case ApiStreamEventType.ContextStats:
            return Object.keys(data).length > 0;
        default:
            return true;
    }
}

function toIsoString(value: Date): string {
    if (value instanceof Date) return value.toISOString();
    const raw = String(value);
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
    const parsed = new Date(hasTimezone ? raw : `${raw}Z`);
    return isNaN(parsed.getTime()) ? raw : parsed.toISOString();
}

function messageType(messageType: string): EngineMessage["type"] {
    if (messageType === ApiMessageTypes.Table) return "table";
    if (messageType === ApiMessageTypes.Error) return "error";
    return "markdown";
}

function visiblePartsMarkdown(parts: ApiMessagePart[]): string {
    let markdown = "";
    for (const part of parts) {
        let partMarkdown = "";
        if (part.kind === "text") {
            partMarkdown = String(part.payload.text ?? "");
        } else if (part.kind === "question") {
            partMarkdown = questionMarkdown(part.payload as unknown as MarvinQuestion);
        } else if (part.kind === "action") {
            partMarkdown = actionMarkdown(part.payload as unknown as MarvinAction);
        }
        if (!partMarkdown) continue;
        if (part.kind !== "text" && markdown) markdown += "\n\n";
        markdown += partMarkdown;
        if (part.kind !== "text") markdown += "\n\n";
    }
    return markdown;
}

function messageReferences(message: ApiMessage): Record<string, ApiReference> {
    return message.references instanceof Map
        ? Object.fromEntries(message.references)
        : (Object.fromEntries(Object.entries(message.references ?? {})) as Record<
              string,
              ApiReference
          >);
}

function messageSegments(
    message: ApiMessage,
    resolver: ConversationReferenceResolver | undefined,
): EngineSegment[] {
    const references = messageReferences(message);
    return normalizedSegments(message.message, false, references, message.files ?? [], resolver);
}

function normalizeMessage(
    message: ApiMessage,
    resolver: ConversationReferenceResolver | undefined,
    conversationId: string,
    omitLegacyBesideTurn = false,
): RuntimeMessage {
    const role = message.user.id === MARVIN_USER_ID ? "marvin" : "user";
    const parts = readMessageParts(message.parts, message.id);
    const runId =
        typeof message.requestId === "string" && message.requestId ? message.requestId : undefined;
    const turn =
        role === "marvin"
            ? savedMessageTurn(
                  parts,
                  message.status,
                  messageReferences(message),
                  message.files ?? [],
                  resolver,
                  { ...(runId ? { runId } : {}), conversationId, messageId: message.id },
              )
            : undefined;
    const type = messageType(message.messageType);
    return {
        id: message.id,
        role,
        type,
        segments:
            omitLegacyBesideTurn && turn && type !== "error"
                ? []
                : messageSegments(message, resolver),
        createdAt: toIsoString(message.createdAt),
        parts,
        ...(runId ? { runId } : {}),
        ...(message.status ? { status: message.status } : {}),
        ...(message.status === "error" && message.message ? { errorMessage: message.message } : {}),
        ...(turn ? { turn } : {}),
    };
}

function normalizeConversation(
    conversation: ApiConversation,
    resolver: ConversationReferenceResolver | undefined,
    omitLegacyBesideTurn = false,
): RuntimeConversation {
    assertConversation(conversation);
    return {
        id: conversation.id,
        title: conversation.title,
        messages: conversation.messages.map((message) =>
            normalizeMessage(message, resolver, conversation.id, omitLegacyBesideTurn),
        ),
    };
}

export function toEngineConversation(conversation: RuntimeConversation): EngineConversation {
    return {
        id: conversation.id,
        title: conversation.title,
        messages: conversation.messages.map(
            ({ parts: _parts, errorMessage: _errorMessage, ...message }) => {
                const createdAtMs = Date.parse(message.createdAt);
                return Number.isFinite(createdAtMs) ? { ...message, createdAtMs } : message;
            },
        ),
    };
}

function conversationReferences(conversation: ApiConversation): ApiReference[] {
    return conversation.messages.flatMap((message) =>
        message.references instanceof Map
            ? Array.from(message.references.values())
            : Object.values(message.references ?? {}),
    );
}

function summarizeConversation(conversation: ApiConversation): EngineConversationSummary {
    assertConversation(conversation);
    const lastActivity =
        conversation.messages.length === 0
            ? new Date(conversation.updatedAt)
            : conversation.messages.reduce<Date>(
                  (latest, message) =>
                      new Date(message.createdAt).getTime() > latest.getTime()
                          ? new Date(message.createdAt)
                          : latest,
                  new Date(0),
              );
    return {
        id: conversation.id,
        title: conversation.title,
        lastActivityAt: toIsoString(lastActivity),
    };
}
export interface NativeRunStreamState {
    protocol: StreamProtocol | null;
    parts: RunStreamState;
    legacy: LegacyRunStreamState;
}

export function createNativeRunStreamState(): NativeRunStreamState {
    return {
        protocol: null,
        parts: initialStreamState,
        legacy: initialLegacyStreamState,
    };
}

export function reduceNativeRunStreamState(
    stream: NativeRunStreamState,
    event: ApiStreamEvent,
): NativeRunStreamState {
    const protocol = stream.protocol ?? streamProtocolForEventType(event.type);
    if (protocol === "parts") {
        return {
            ...stream,
            protocol,
            parts: handleStreamEvent(stream.parts, event),
        };
    }
    if (protocol === "legacy") {
        return {
            ...stream,
            protocol,
            legacy: reduceLegacyStreamEvent(stream.legacy, event),
        };
    }
    return {
        protocol: null,
        parts: handleStreamEvent(stream.parts, event),
        legacy: reduceLegacyStreamEvent(stream.legacy, event),
    };
}

export function nativeStreamMarkdown(stream: NativeRunStreamState): string {
    if (stream.protocol === "legacy") return legacyStreamMarkdown(stream.legacy);
    return visiblePartsMarkdown(stream.parts.parts);
}

function selectedStreamState(stream: NativeRunStreamState): RunStreamState | LegacyRunStreamState {
    return stream.protocol === "legacy" ? stream.legacy : stream.parts;
}

function settleNativeRunStream(
    stream: NativeRunStreamState,
    status: "ok" | "cancelled" | "error",
    error: string | null = null,
): NativeRunStreamState {
    const update = {
        isStreaming: false,
        isDone: true,
        status,
        error,
    };
    if (stream.protocol === "legacy") {
        return { ...stream, legacy: { ...stream.legacy, ...update } };
    }
    if (stream.protocol === "parts") {
        return { ...stream, parts: { ...stream.parts, ...update } };
    }
    return {
        ...stream,
        parts: { ...stream.parts, ...update },
        legacy: { ...stream.legacy, ...update },
    };
}

function runReferences(stream: NativeRunStreamState): {
    referenceMap: Record<string, ApiReference>;
    files: ApiFile[];
} {
    if (stream.protocol === "legacy") {
        return {
            referenceMap: stream.legacy.referenceMap,
            files: stream.legacy.files,
        };
    }
    return {
        referenceMap: partsReferenceMap(stream.parts.parts),
        files: partsFiles(stream.parts.parts),
    };
}

function startNativeRunStream(userQuery: string): NativeRunStreamState {
    return {
        protocol: null,
        parts: {
            ...initialStreamState,
            isStreaming: true,
            userQuery,
        },
        legacy: {
            ...initialLegacyStreamState,
            isStreaming: true,
            userQuery,
        },
    };
}

interface ActiveRun {
    generation: number;
    draft: ConversationRunDraft;
    serverUserMessageId: string | null;
    conversationId: string;
    runId: string | null;
    marvinMessageId: string | null;
    stream: NativeRunStreamState;
    cleanup: (() => void) | null;
    /** Consecutive stream opens that lost their connection at once (see streamReopen.ts). */
    quickStreamFailures: number;
    reopenTimer: ReturnType<typeof setTimeout> | null;
    resolvedReferenceKeys: Set<string>;
    reconciled: boolean;
    localCreatedAt: string | null;
    terminalFailureHandled: boolean;
    lastSnapshotAt: number;
    /** Publishes a delta the interval held back once the interval ends, so none waits on the next event. */
    trailingSnapshotTimer: ReturnType<typeof setTimeout> | null;
    fallbackStatusText: string;
    cancelled: boolean;
    highWaterMarkdown: string | null;
    /** Once true, the first-content snapshot has been published without reparsing prior deltas. */
    hasStreamContent: boolean;
    authoritativeLoadCorrelated: boolean;
    /** When this engine first saw the turn and each step in flight: its header clock counts from it. */
    turnClock: TurnClock;
}

interface LocalTerminalTurn {
    run: ActiveRun;
    revision: number;
    pendingCorrelation: boolean;
}

interface ConversationMutationState {
    revision: number;
    terminalTurns: LocalTerminalTurn[];
}

function statusFromStream(stream: NativeRunStreamState): EngineStreamSnapshot["status"] {
    const selected = selectedStreamState(stream);
    if (!selected.isDone) return "streaming";
    return selected.status ?? "ok";
}

function streamSegments(
    run: ActiveRun,
    resolver: ConversationReferenceResolver | undefined,
): EngineSegment[] {
    const selected = selectedStreamState(run.stream);
    const references = runReferences(run.stream);
    const processed = preprocessMessageMarkdown(nativeStreamMarkdown(run.stream), !selected.isDone);
    if (selected.isDone) {
        run.highWaterMarkdown = null;
    } else if (run.highWaterMarkdown === null || processed.length >= run.highWaterMarkdown.length) {
        run.highWaterMarkdown = processed;
    }
    return segmentsFromProcessedMarkdown(
        run.highWaterMarkdown ?? processed,
        references.referenceMap,
        references.files,
        resolver,
    );
}

/** The run's turn so far, from its parts; a legacy stream has no parts and no turn. */
function streamTurn(
    run: ActiveRun,
    status: EngineStreamSnapshot["status"],
    resolver: ConversationReferenceResolver | undefined,
): EngineTurn | undefined {
    if (run.stream.protocol === "legacy") return undefined;
    const references = runReferences(run.stream);
    return buildEngineTurn({
        parts: run.stream.parts.parts,
        phase: turnPhaseOf(status),
        referenceMap: references.referenceMap,
        files: references.files,
        resolver,
        clock: run.turnClock,
        groupRepeats: true,
        ...(run.runId ? { runId: run.runId } : {}),
        ...(run.conversationId ? { conversationId: run.conversationId } : {}),
        ...(run.marvinMessageId ? { messageId: run.marvinMessageId } : {}),
    });
}

function thinkingFromStream(stream: NativeRunStreamState): EngineThinking {
    if (stream.protocol === "legacy") {
        return {
            isThinking: !stream.legacy.isDone && stream.legacy.thinking.isThinking,
            durationMs: stream.legacy.thinking.durationMs,
        };
    }
    const state = stream.parts;
    if (!state.isStreaming) return { isThinking: false, durationMs: null };
    const hasAnswer = partsMarkdown(state.parts).length > 0;
    let lastIsThinking = false;
    for (let i = state.parts.length - 1; i >= 0; i--) {
        const part = state.parts[i];
        if (part.kind === "text") break;
        if (part.kind === "activity") {
            lastIsThinking = (part.payload as unknown as ActivityPartPayload).kind === "thinking";
            break;
        }
    }
    return { isThinking: !hasAnswer || lastIsThinking, durationMs: null };
}

function runStatusText(run: ActiveRun): string {
    const references = runReferences(run.stream);
    const processor = new ReferenceProcessor(
        new Map(Object.entries(references.referenceMap)),
        references.files,
    );
    let recentToolCalls: string[] = [];

    if (run.stream.protocol === "legacy") {
        const { thinking, steps } = run.stream.legacy;
        if (!run.stream.legacy.isDone && thinking.isThinking) {
            const lines = thinking.buffer
                .trim()
                .split("\n")
                .filter((line) => line.trim());
            return lines.length > 0
                ? resolveThinkingReferences(stripBold(lines[lines.length - 1]), processor)
                : run.fallbackStatusText;
        }
        for (let i = steps.length - 1; i >= 0 && steps[i].code === "tool_call"; i--) {
            recentToolCalls.unshift(steps[i].message);
        }
    } else {
        const state = run.stream.parts;
        const lines = activityPartsToTrace(state.parts);
        const last = lines[lines.length - 1];
        if (state.isStreaming && last?.kind === "thinking") {
            return last.text.trim()
                ? resolveThinkingReferences(stripBold(last.text), processor)
                : run.fallbackStatusText;
        }
        for (let i = lines.length - 1; i >= 0 && lines[i].kind === "tool"; i--) {
            recentToolCalls.unshift(lines[i].text);
        }
    }

    if (recentToolCalls.length > 1) {
        const counts = new Map<string, number>();
        for (const message of recentToolCalls) {
            counts.set(message, (counts.get(message) || 0) + 1);
        }
        return Array.from(counts.entries())
            .map(([message, count]) => (count > 1 ? `${message} (${count})` : message))
            .join("\n");
    }
    return recentToolCalls[0] ?? run.fallbackStatusText;
}

const STREAM_SNAPSHOT_INTERVAL_MS = 125;
export function createConversationRuntime(
    ports: ConversationRuntimePorts,
    options: ConversationRuntimeOptions = {},
): ConversationRuntime {
    let state: ConversationRuntimeState = {
        conversations: [],
        selectedConversationId: null,
        selectedConversation: null,
        activeRun: null,
        error: null,
        retryDraft: null,
    };
    const listeners = new Set<() => void>();
    const eventListeners = new Set<(event: ConversationRuntimeEvent) => void>();
    let operationGeneration = 0;
    let resetGeneration = 0;
    let listEpoch = 0;
    let loadEpoch = 0;
    let activeRun: ActiveRun | null = null;
    const conversationMutations = new Map<string, ConversationMutationState>();
    let disposed = false;
    const now = options.now ?? (() => performance.now());
    const fallbackStatusText = options.fallbackStatusText ?? (() => "Thinking...");

    const update = (next: ConversationRuntimeState) => {
        state = next;
        for (const listener of Array.from(listeners)) listener();
    };

    const emitEvent = (event: ConversationRuntimeEvent) => {
        for (const listener of Array.from(eventListeners)) listener(event);
    };

    const buildSnapshot = (run: ActiveRun, statusOverride?: "creating"): EngineStreamSnapshot => {
        const status = statusOverride ?? statusFromStream(run.stream);
        const turn = streamTurn(run, status, options.referenceResolver);
        const legacy = !(options.omitLegacyBesideTurn && turn);
        const snapshot: EngineStreamSnapshot = {
            commandId: run.draft.commandId,
            conversationId: run.conversationId,
            userQuery: run.draft.text,
            thinking: thinkingFromStream(run.stream),
            segments: legacy ? streamSegments(run, options.referenceResolver) : [],
            status,
            statusText: legacy ? runStatusText(run) : "",
        };
        const selected = selectedStreamState(run.stream);
        if (run.runId) {
            snapshot.runId = run.runId;
            snapshot.lastSeq = selected.lastSeq;
        }
        if (selected.error) snapshot.errorMessage = selected.error;
        // Last, so the fields a build before `turn` reads keep their bytes and order.
        if (turn) snapshot.turn = turn;
        return snapshot;
    };

    const publishActiveRun = (
        run: ActiveRun,
        statusOverride?: "creating",
        reportSnapshot = true,
    ) => {
        const snapshot = buildSnapshot(run, statusOverride);
        update({
            ...state,
            selectedConversationId: state.selectedConversationId || run.conversationId || null,
            activeRun: {
                commandId: run.draft.commandId,
                conversationId: run.conversationId,
                runId: run.runId,
                marvinMessageId: run.marvinMessageId,
                userQuery: run.draft.text,
                parts: run.stream.parts.parts,
                lastSeq: selectedStreamState(run.stream).lastSeq,
                status: snapshot.status,
                snapshot,
            },
            error: selectedStreamState(run.stream).error,
        });
        if (reportSnapshot) {
            run.lastSnapshotAt = now();
            clearTrailingSnapshot(run);
            emitEvent({ type: "snapshot", snapshot });
        }
    };

    const clearTrailingSnapshot = (run: ActiveRun) => {
        if (run.trailingSnapshotTimer !== null) clearTimeout(run.trailingSnapshotTimer);
        run.trailingSnapshotTimer = null;
    };

    /** A delta held back by the interval shows when the interval ends, not with the next event. */
    const scheduleTrailingSnapshot = (run: ActiveRun) => {
        if (run.trailingSnapshotTimer !== null) return;
        const wait = Math.max(0, STREAM_SNAPSHOT_INTERVAL_MS - (now() - run.lastSnapshotAt));
        run.trailingSnapshotTimer = setTimeout(() => {
            run.trailingSnapshotTimer = null;
            if (disposed || !isCurrentRun(run) || run.cancelled) return;
            if (selectedStreamState(run.stream).isDone) return;
            publishActiveRun(run);
        }, wait);
    };

    const isCurrentRun = (run: ActiveRun) =>
        activeRun === run && operationGeneration === run.generation;

    const closeRunStream = (run: ActiveRun) => {
        run.cleanup?.();
        run.cleanup = null;
        clearTrailingSnapshot(run);
        if (run.reopenTimer !== null) clearTimeout(run.reopenTimer);
        run.reopenTimer = null;
    };

    const terminateRunWithError = (run: ActiveRun, message: string) => {
        if (run.terminalFailureHandled) return;
        run.terminalFailureHandled = true;
        closeRunStream(run);
        if (run.runId) void ports.cancelRun(run.runId).catch(() => undefined);
        run.stream = settleNativeRunStream(run.stream, "error", message);
        emitEvent({ type: "failure", commandId: run.draft.commandId, message });
        publishActiveRun(run);
    };

    const conversationWithTerminalRun = (
        run: ActiveRun,
        current: RuntimeConversation,
    ): RuntimeConversation => {
        const selected = selectedStreamState(run.stream);
        if (!selected.isDone) return current;

        const messages = [...current.messages];
        const marvinId = run.marvinMessageId ?? `marvin:${run.draft.commandId}`;
        const userId = run.serverUserMessageId ?? `user:${run.draft.commandId}`;
        const createdAt = run.localCreatedAt ?? new Date().toISOString();
        run.localCreatedAt = createdAt;
        if (!messages.some((message) => message.id === userId)) {
            const localUser: RuntimeMessage = {
                id: userId,
                role: "user",
                type: "markdown",
                segments: normalizedSegments(run.draft.text, false, {}, [], undefined),
                createdAt,
                parts: [],
            };
            const existingMarvinIndex = messages.findIndex((message) => message.id === marvinId);
            if (existingMarvinIndex === -1) {
                messages.push(localUser);
            } else {
                messages.splice(existingMarvinIndex, 0, localUser);
            }
        }

        const marvinIndex = messages.findIndex((message) => message.id === marvinId);
        const snapshot = buildSnapshot(run);
        const localMarvin: RuntimeMessage = {
            id: marvinId,
            role: "marvin" as const,
            type: selected.status === "error" ? "error" : "markdown",
            segments: snapshot.segments,
            createdAt,
            parts: [...run.stream.parts.parts],
            ...(run.runId ? { runId: run.runId } : {}),
            ...(selected.error ? { errorMessage: selected.error } : {}),
            ...(snapshot.turn ? { turn: snapshot.turn } : {}),
        };
        if (marvinIndex === -1) {
            messages.push(localMarvin);
        } else {
            messages[marvinIndex] = {
                ...messages[marvinIndex],
                type: localMarvin.type,
                segments: localMarvin.segments,
                parts: localMarvin.parts,
                runId: localMarvin.runId,
                errorMessage: localMarvin.errorMessage,
                turn: localMarvin.turn,
            };
        }
        return { ...current, messages };
    };

    const localRunsForConversation = (conversationId: string): ActiveRun[] => {
        const terminalRuns =
            conversationMutations.get(conversationId)?.terminalTurns.map((turn) => turn.run) ?? [];
        if (activeRun?.conversationId !== conversationId || terminalRuns.includes(activeRun)) {
            return terminalRuns;
        }
        return [...terminalRuns, activeRun];
    };

    const correlateLoadedRun = (
        run: ActiveRun,
        loaded: ApiConversation,
        normalized: RuntimeConversation,
    ) => {
        if (!run.marvinMessageId) return;
        if (!run.serverUserMessageId) {
            const placeholder = loaded.messages.find(
                (message) => message.id === run.marvinMessageId,
            );
            const parentId = placeholder?.parentId;
            const parent =
                typeof parentId === "string"
                    ? normalized.messages.find(
                          (message) => message.id === parentId && message.role === "user",
                      )
                    : undefined;
            if (parent) run.serverUserMessageId = parent.id;
        }
        if (!run.serverUserMessageId) return;
        const hasUser = normalized.messages.some(
            (message) => message.id === run.serverUserMessageId && message.role === "user",
        );
        const hasMarvin = normalized.messages.some(
            (message) => message.id === run.marvinMessageId && message.role === "marvin",
        );
        if (hasUser && hasMarvin) run.authoritativeLoadCorrelated = true;
    };

    const projectLoadedConversation = (
        conversationId: string,
        loaded: ApiConversation,
        revisionAtStart: number,
    ): RuntimeConversation => {
        const normalized = normalizeConversation(
            loaded,
            options.referenceResolver,
            options.omitLegacyBesideTurn,
        );
        for (const run of localRunsForConversation(conversationId)) {
            correlateLoadedRun(run, loaded, normalized);
        }

        const mutation = conversationMutations.get(conversationId);
        if (!mutation) return normalized;
        return mutation.terminalTurns.reduce((projected, turn) => {
            const marvinId = turn.run.marvinMessageId ?? `marvin:${turn.run.draft.commandId}`;
            const hasMarvin = normalized.messages.some(
                (message) => message.id === marvinId && message.role === "marvin",
            );
            const retainIfAbsent = turn.pendingCorrelation || turn.revision > revisionAtStart;
            if (turn.run.authoritativeLoadCorrelated) {
                turn.pendingCorrelation = false;
            }
            return hasMarvin || retainIfAbsent
                ? conversationWithTerminalRun(turn.run, projected)
                : projected;
        }, normalized);
    };

    const rememberTerminalRun = (run: ActiveRun) => {
        const mutation = conversationMutations.get(run.conversationId) ?? {
            revision: 0,
            terminalTurns: [],
        };
        const existing = mutation.terminalTurns.find((turn) => turn.run === run);
        if (existing) return existing;
        const revision = mutation.revision + 1;
        const turn: LocalTerminalTurn = {
            run,
            revision,
            pendingCorrelation: !run.authoritativeLoadCorrelated,
        };
        conversationMutations.set(run.conversationId, {
            revision,
            terminalTurns: [...mutation.terminalTurns, turn],
        });
        return turn;
    };

    const reviseTerminalRun = (run: ActiveRun) => {
        const mutation = conversationMutations.get(run.conversationId);
        const turn = mutation?.terminalTurns.find((candidate) => candidate.run === run);
        if (!mutation || !turn) return;
        const revision = mutation.revision + 1;
        turn.revision = revision;
        conversationMutations.set(run.conversationId, { ...mutation, revision });
    };

    const reconcileTerminalRun = (run: ActiveRun) => {
        const selected = selectedStreamState(run.stream);
        if (!selected.isDone || run.reconciled || !run.conversationId) return;
        run.reconciled = true;
        rememberTerminalRun(run);
        const retryDraft =
            selected.status === "ok" || selected.status === "cancelled" ? null : state.retryDraft;
        if (
            state.selectedConversationId !== null &&
            state.selectedConversationId !== run.conversationId
        ) {
            update({ ...state, retryDraft });
            return;
        }
        const current =
            state.selectedConversation?.id === run.conversationId
                ? state.selectedConversation
                : { id: run.conversationId, title: "", messages: [] };
        update({
            ...state,
            selectedConversationId: run.conversationId,
            selectedConversation: conversationWithTerminalRun(run, current),
            retryDraft,
        });
    };

    const refreshReconciledMessage = (run: ActiveRun) => {
        if (!run.reconciled || state.selectedConversation?.id !== run.conversationId) {
            return;
        }
        const marvinId = run.marvinMessageId ?? `marvin:${run.draft.commandId}`;
        const snapshot = buildSnapshot(run);
        const messages = state.selectedConversation.messages.map((message) =>
            message.id === marvinId
                ? { ...message, segments: snapshot.segments, turn: snapshot.turn }
                : message,
        );
        reviseTerminalRun(run);
        update({
            ...state,
            selectedConversation: { ...state.selectedConversation, messages },
        });
    };

    const onStreamEvent = (run: ActiveRun, event: ApiStreamEvent) => {
        if (!isCurrentRun(run) || selectedStreamState(run.stream).isDone) return;
        try {
            if (!isValidStreamEvent(event)) {
                terminateRunWithError(run, "Invalid stream event");
                return;
            }
            if (!partEventApplies(run.stream.parts.parts, event)) return;
            if (event.type === ApiStreamEventType.PartUpdated) {
                const updated = run.stream.parts.parts.find((part) => part.seq === event.data.seq);
                if (updated?.kind === "question" || updated?.kind === "action") {
                    run.highWaterMarkdown = null;
                }
            }
            const hadContent = run.hasStreamContent;
            run.stream = reduceNativeRunStreamState(run.stream, event);
            const selected = selectedStreamState(run.stream);
            // The initial non-empty answer should appear without waiting for the interval,
            // but do this markdown projection at most once. Subsequent high-frequency
            // events are gated before buildSnapshot performs its full projection.
            if (!hadContent) run.hasStreamContent = nativeStreamMarkdown(run.stream).length > 0;
            const highFrequency =
                event.type === ApiStreamEventType.PartUpdated ||
                event.type === ApiStreamEventType.ContentBlockDelta ||
                event.type === ApiStreamEventType.ThinkingDelta;
            const reportSnapshot =
                selected.isDone ||
                (!hadContent && run.hasStreamContent) ||
                !highFrequency ||
                now() - run.lastSnapshotAt >= STREAM_SNAPSHOT_INTERVAL_MS;
            if (selected.isDone) reconcileTerminalRun(run);
            if (reportSnapshot) publishActiveRun(run);
            else scheduleTrailingSnapshot(run);

            const resolver = options.referenceResolver;
            const references = Object.values(runReferences(run.stream).referenceMap);
            const referencesToResolve = references.filter((reference) => {
                const keys = [
                    `file:${reference.fileId}`,
                    ...(reference.sectionUuid ? [`section:${reference.sectionUuid}`] : []),
                ];
                const hasNewKey = keys.some((key) => !run.resolvedReferenceKeys.has(key));
                for (const key of keys) run.resolvedReferenceKeys.add(key);
                return hasNewKey;
            });
            if (resolver && referencesToResolve.length > 0) {
                const resolutionGeneration = resetGeneration;
                const resolutionConversationId = run.conversationId;
                void resolver
                    .resolve(referencesToResolve)
                    .then(() => {
                        if (
                            !disposed &&
                            resolutionGeneration === resetGeneration &&
                            state.selectedConversation?.id === resolutionConversationId
                        ) {
                            refreshReconciledMessage(run);
                        }
                        if (isCurrentRun(run)) publishActiveRun(run);
                    })
                    .catch(() => {
                        // Raw-file citation targets remain usable after resolver failure.
                    });
            }
        } catch {
            terminateRunWithError(run, "Invalid stream event");
        }
    };

    /**
     * Opens the run's stream (after `afterSeq` when given) and owns its connection: a lost
     * connection reopens from the last event applied after a short pause instead of ending
     * the turn, matching the web stream owner (`openStream` in hooks/useRunStream.ts). Only
     * a stream that keeps failing at once (the run is gone, the network is down) ends the
     * turn with the connection error. Without this an iPad that walked through a dead spot
     * showed "Connection lost" for an answer the server went on to finish and save.
     */
    const openRunStream = (run: ActiveRun, runId: string, afterSeq?: number) => {
        const openedAt = Date.now();
        let delivered = false;
        let lost: "reopen" | "fail" | null = null;
        const onEvent = (event: ApiStreamEvent) => {
            if (!isCurrentRun(run) || run.cancelled) return;
            if (isConnectionLostEvent(event)) {
                // A refusal (signed out, no access, an app the project no longer serves) gets
                // the same answer on every reopen: the turn ends with the server's words.
                if (lost === null && isStreamRefused(event)) lost = "fail";
                if (lost === null) {
                    const quick = !delivered && Date.now() - openedAt < HEALTHY_STREAM_MS;
                    run.quickStreamFailures = quick ? run.quickStreamFailures + 1 : 0;
                    lost = run.quickStreamFailures > MAX_QUICK_REOPENS ? "fail" : "reopen";
                    if (lost === "reopen") {
                        closeRunStream(run);
                        run.reopenTimer = setTimeout(
                            () => {
                                run.reopenTimer = null;
                                if (!isCurrentRun(run) || run.cancelled || disposed) return;
                                if (selectedStreamState(run.stream).isDone) return;
                                openRunStream(run, runId, selectedStreamState(run.stream).lastSeq);
                            },
                            reopenDelayMs(quick, run.quickStreamFailures),
                        );
                    }
                }
                // Out of reopens: the turn ends with the connection error, as before.
                if (lost === "fail") onStreamEvent(run, event);
                return;
            }
            delivered = true;
            run.quickStreamFailures = 0;
            onStreamEvent(run, event);
        };
        run.cleanup = ports.streamRun(
            runId,
            onEvent,
            afterSeq === undefined ? undefined : { afterSeq },
        );
    };

    const retireRun = (run: ActiveRun) => {
        run.cancelled = true;
        closeRunStream(run);
        if (run.runId) {
            void ports.cancelRun(run.runId).catch(() => undefined);
        }
    };
    const activateRun = (
        draft: ConversationRunDraft,
        statusOverride?: "creating",
        serverUserMessageId: string | null = null,
    ): ActiveRun => {
        if (activeRun && !selectedStreamState(activeRun.stream).isDone) retireRun(activeRun);
        operationGeneration += 1;
        const run: ActiveRun = {
            lastSnapshotAt: 0,
            trailingSnapshotTimer: null,
            fallbackStatusText: fallbackStatusText(),
            generation: operationGeneration,
            draft,
            conversationId: draft.conversationId,
            runId: null,
            marvinMessageId: null,
            stream: startNativeRunStream(draft.text),
            cleanup: null,
            quickStreamFailures: 0,
            reopenTimer: null,
            cancelled: false,
            resolvedReferenceKeys: new Set(),
            reconciled: false,
            localCreatedAt: null,
            serverUserMessageId,
            terminalFailureHandled: false,
            highWaterMarkdown: null,
            hasStreamContent: false,
            authoritativeLoadCorrelated: false,
            turnClock: createTurnClock(),
        };
        activeRun = run;
        update({
            ...state,
            selectedConversationId: draft.conversationId || state.selectedConversationId,
            retryDraft: draft,
            error: null,
        });
        publishActiveRun(run, statusOverride);
        return run;
    };

    const failRun = (run: ActiveRun, error: unknown) => {
        const message = error instanceof Error ? error.message : "Failed to start conversation run";
        terminateRunWithError(run, message);
    };

    const prepareRunFiles = async (run: ActiveRun): Promise<boolean> => {
        if (!run.draft.prepareFiles) return true;
        try {
            const files = await run.draft.prepareFiles();
            if (!isCurrentRun(run) || run.cancelled || disposed) return false;
            run.draft = { ...run.draft, files, prepareFiles: undefined };
            update({ ...state, retryDraft: run.draft });
            return true;
        } catch (error) {
            if (isCurrentRun(run) && !run.cancelled) failRun(run, error);
            return false;
        }
    };

    const prepareRunDocumentFilters = async (run: ActiveRun): Promise<boolean> => {
        if (!run.draft.prepareDocumentFilters) return true;
        try {
            const documentFilters = await run.draft.prepareDocumentFilters();
            if (!isCurrentRun(run) || run.cancelled || disposed) return false;
            run.draft = { ...run.draft, documentFilters, prepareDocumentFilters: undefined };
            update({ ...state, retryDraft: run.draft });
            return true;
        } catch (error) {
            if (isCurrentRun(run) && !run.cancelled) failRun(run, error);
            return false;
        }
    };

    const beginRun = async (run: ActiveRun): Promise<boolean> => {
        if (!isCurrentRun(run) || run.cancelled || disposed) return false;
        let createdRunId: string | null = null;
        try {
            const created = await ports.createRun({
                query: run.draft.text,
                conversationId: run.conversationId,
                ...(run.draft.files && run.draft.files.length > 0
                    ? { files: run.draft.files }
                    : {}),
                ...(run.draft.depth ? { depth: run.draft.depth } : {}),
                ...(run.draft.answersQuestion
                    ? { answersQuestion: run.draft.answersQuestion }
                    : {}),
            });
            createdRunId = created.runId;
            if (!isCurrentRun(run) || run.cancelled || disposed) {
                void ports.cancelRun(created.runId).catch(() => undefined);
                return false;
            }
            if (run.draft.answersQuestion) {
                // The created run claimed the card; a retry of this draft is a new message.
                run.draft = { ...run.draft, answersQuestion: undefined };
                update({ ...state, retryDraft: run.draft });
            }

            run.runId = created.runId;
            run.marvinMessageId = created.marvinMessageId;
            if (created.userMessageId !== undefined) {
                run.serverUserMessageId = created.userMessageId;
            }
            publishActiveRun(run);
            openRunStream(run, created.runId);
            return true;
        } catch (error) {
            if (!isCurrentRun(run) || run.cancelled) return false;
            if (createdRunId) {
                void ports.cancelRun(createdRunId).catch(() => undefined);
            }
            failRun(run, error);
            return false;
        }
    };

    const send = async (
        draft: ConversationRunDraft,
        serverUserMessageId: string | null = null,
    ): Promise<boolean> => {
        if (disposed) return false;
        const run = activateRun(draft, undefined, serverUserMessageId);
        if (run.draft.prepareFiles && !(await prepareRunFiles(run))) return false;
        return beginRun(run);
    };

    const createAndRun = async (draft: NewConversationRunDraft): Promise<boolean> => {
        if (disposed) return false;
        const run = activateRun(
            {
                ...draft,
                conversationId: "",
                createConversation: true,
            },
            "creating",
        );
        if (run.draft.prepareFiles && !(await prepareRunFiles(run))) return false;
        if (run.draft.prepareDocumentFilters && !(await prepareRunDocumentFilters(run))) {
            return false;
        }
        try {
            const createdConversation = await ports.createConversation(
                draft.text,
                ApiMessageTypes.Markdown,
                run.draft.documentFilters,
            );
            if (!isCurrentRun(run) || run.cancelled || disposed) return false;
            run.conversationId = createdConversation.id;
            run.draft = {
                ...run.draft,
                conversationId: createdConversation.id,
                createConversation: false,
            };
            const normalizedConversation = normalizeConversation(
                createdConversation,
                options.referenceResolver,
                options.omitLegacyBesideTurn,
            );
            update({
                ...state,
                selectedConversationId: createdConversation.id,
                selectedConversation: normalizedConversation,
                retryDraft: run.draft,
            });
            publishActiveRun(run);
            return beginRun(run);
        } catch (error) {
            if (!isCurrentRun(run) || run.cancelled) return false;
            failRun(run, error);
            return false;
        }
    };

    // A stream can stall without an error while the app is suspended: a connection
    // the network dropped meanwhile delivers neither data nor a failure, so the run
    // would show "thinking" until the screen is reopened although the server finished
    // and saved the answer. The caller resumes on return to the foreground; the server
    // replays every event after `lastSeq`, `done` included (each reducer also drops seqs
    // it has seen). A reopen already waiting out its backoff happens now instead, with a
    // fresh budget: the network the backoff was waiting for is usually back.
    const resume = () => {
        const run = activeRun;
        if (disposed || !run || !run.runId || run.cancelled) return;
        if (selectedStreamState(run.stream).isDone) return;
        closeRunStream(run);
        run.quickStreamFailures = 0;
        openRunStream(run, run.runId, selectedStreamState(run.stream).lastSeq);
    };

    const attach = (attached: AttachConversationRun) => {
        if (disposed) return;
        const run = activateRun({
            commandId: attached.commandId,
            conversationId: attached.conversationId,
            text: attached.userQuery,
        });
        run.runId = attached.runId;
        run.marvinMessageId = attached.marvinMessageId ?? null;
        publishActiveRun(run);
        openRunStream(run, attached.runId, 0);
    };

    const cancel = async (): Promise<void> => {
        if (disposed) return;
        const run = activeRun;
        if (!run || selectedStreamState(run.stream).isDone) return;
        run.cancelled = true;
        closeRunStream(run);
        run.stream = settleNativeRunStream(run.stream, "cancelled");
        reconcileTerminalRun(run);
        publishActiveRun(run);
        if (run.runId) {
            try {
                await ports.cancelRun(run.runId);
            } catch {
                // The local terminal state remains authoritative.
            }
        }
    };

    const retry = async (): Promise<boolean> => {
        if (disposed) return false;
        const draft = state.retryDraft;
        if (!draft) return false;
        if (draft.createConversation) {
            return createAndRun({
                commandId: draft.commandId,
                text: draft.text,
                files: draft.files,
                depth: draft.depth,
                documentFilters: draft.documentFilters,
                prepareFiles: draft.prepareFiles,
                prepareDocumentFilters: draft.prepareDocumentFilters,
            });
        }
        const retryUserMessageId =
            activeRun?.draft.commandId === draft.commandId &&
            activeRun.conversationId === draft.conversationId
                ? activeRun.serverUserMessageId
                : null;
        return send(draft, retryUserMessageId);
    };

    const resetState = () => {
        operationGeneration += 1;
        resetGeneration += 1;
        listEpoch += 1;
        loadEpoch += 1;
        if (activeRun && !selectedStreamState(activeRun.stream).isDone) retireRun(activeRun);
        activeRun = null;
        conversationMutations.clear();
        update({
            conversations: [],
            selectedConversationId: null,
            selectedConversation: null,
            activeRun: null,
            error: null,
            retryDraft: null,
        });
    };

    const reset = () => {
        if (disposed) return;
        resetState();
    };

    const dispose = () => {
        if (disposed) return;
        resetState();
        disposed = true;
        listeners.clear();
        eventListeners.clear();
    };

    return {
        getState: () => state,
        subscribe: (listener) => {
            if (disposed) return () => undefined;
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        subscribeEvents: (listener) => {
            if (disposed) return () => undefined;
            eventListeners.add(listener);
            return () => eventListeners.delete(listener);
        },
        list: async (commandId) => {
            if (disposed) return false;
            const resetAtStart = resetGeneration;
            const epoch = ++listEpoch;
            try {
                const conversations = await ports.listConversations();
                if (disposed || resetAtStart !== resetGeneration || epoch !== listEpoch) {
                    return false;
                }
                const summaries = conversations.map(summarizeConversation);
                update({ ...state, conversations: summaries, error: null });
                emitEvent({ type: "conversations", commandId, conversations: summaries });
                return true;
            } catch (error) {
                if (disposed || resetAtStart !== resetGeneration || epoch !== listEpoch) {
                    return false;
                }
                const message =
                    error instanceof Error ? error.message : "Failed to list conversations";
                update({ ...state, error: message });
                emitEvent({ type: "failure", commandId, message });
                return false;
            }
        },
        load: async (commandId, conversationId) => {
            if (disposed) return false;
            const resetAtStart = resetGeneration;
            const epoch = ++loadEpoch;
            const revisionAtStart = conversationMutations.get(conversationId)?.revision ?? 0;
            update({ ...state, selectedConversationId: conversationId, error: null });
            try {
                const loaded = await ports.getConversation(conversationId);
                if (
                    disposed ||
                    resetAtStart !== resetGeneration ||
                    epoch !== loadEpoch ||
                    state.selectedConversationId !== conversationId
                ) {
                    return false;
                }
                let normalized = projectLoadedConversation(conversationId, loaded, revisionAtStart);
                update({ ...state, selectedConversation: normalized });
                const references = conversationReferences(loaded);
                const resolves = references.length > 0 && options.referenceResolver !== undefined;
                emitEvent({
                    type: "conversation",
                    commandId,
                    conversation: normalized,
                    ...(resolves ? { provisional: true } : {}),
                });

                if (resolves && options.referenceResolver) {
                    await options.referenceResolver.resolve(references);
                    if (
                        disposed ||
                        resetAtStart !== resetGeneration ||
                        epoch !== loadEpoch ||
                        state.selectedConversationId !== conversationId
                    ) {
                        return false;
                    }
                    normalized = projectLoadedConversation(conversationId, loaded, revisionAtStart);
                    update({ ...state, selectedConversation: normalized });
                    emitEvent({ type: "conversation", commandId, conversation: normalized });
                }
                return true;
            } catch (error) {
                if (
                    disposed ||
                    resetAtStart !== resetGeneration ||
                    epoch !== loadEpoch ||
                    state.selectedConversationId !== conversationId
                ) {
                    return false;
                }
                const message =
                    error instanceof Error ? error.message : "Failed to load conversation";
                update({ ...state, error: message });
                emitEvent({ type: "failure", commandId, message });
                return false;
            }
        },
        send,
        createAndRun,
        cancel,
        attach,
        resume,
        decideAction: (runId, actionId, approve) => {
            if (disposed) return Promise.reject(new Error("Conversation runtime is disposed"));
            return ports.decideAction(runId, actionId, approve);
        },
        retry,
        reset,
        dispose,
    };
}
